
import React, { useMemo } from 'react';
import { Chip, Tooltip, IconButton } from '@mui/material';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import useCustomerTier from '../../hooks/useCustomerTier';
import { useNavigate } from 'react-router-dom';

const CHIP_THEMES = {
  Basic: {
    gradient: 'linear-gradient(90deg, #885E4B 0%, #CCA18E 48%, #865C48 100%)',
    fg: '#FFF9F6',
    shadow: '0 8px 20px rgba(136, 94, 75, 0.45)',
    icon: '#FFE3D0',
  },
  Standard: {
    gradient: 'linear-gradient(90deg, #515151 0%, #BFBFBF 48%, #A2A2A2 100%)',
    fg: '#F7F7F7',
    shadow: '0 8px 20px rgba(92, 92, 92, 0.35)',
    icon: '#FFFFFF',
  },
  Advanced: {
    gradient: 'linear-gradient(90deg, #44371E 0%, #D0A60E 48%, #533E13 100%)',
    fg: '#FFF9E2',
    shadow: '0 8px 24px rgba(68, 55, 30, 0.42)',
    icon: '#FFE9A8',
  },
  Elite: {
    gradient: 'linear-gradient(90deg, #04149D 0%, #1E95C8 48%, #04149D 100%)',
    fg: '#F3FAFF',
    shadow: '0 10px 28px rgba(4, 20, 157, 0.45)',
    icon: '#B8E9FF',
  },
};

export default function AdvisorEmailChip({ email, size = 'medium', iconOnly = false, compact = false }) {
  const { tier } = useCustomerTier();
  const navigate = useNavigate();

  const chipTheme = useMemo(() => {
    if (!tier) return null;
    return CHIP_THEMES[tier] ?? null;
  }, [tier]);

  if (!email) return null;

  const displayEmail = `Consultant: ${email}`;

  const handleNavigate = (event) => {
    event.preventDefault();
    navigate('/feedback-support');
  };

  const effectiveSize = compact ? 'small' : size;

  if (iconOnly) {
    return (
      <Tooltip title={displayEmail} enterDelay={300} disableInteractive>
        <IconButton
          size="small"
          aria-label={displayEmail}
          onClick={handleNavigate}
          sx={{
            width: compact ? 26 : 32,
            height: compact ? 26 : 32,
            bgcolor: chipTheme ? 'transparent' : undefined,
            color: chipTheme ? chipTheme.fg : 'inherit',
          }}
        >
          <EmailOutlinedIcon
            fontSize="small"
            sx={{ color: chipTheme ? chipTheme.icon : 'inherit' }}
          />
        </IconButton>
      </Tooltip>
    );
  }

  return (
    <Tooltip title={displayEmail} enterDelay={300} disableInteractive>
      <Chip
        label={displayEmail}
        size={effectiveSize}
        onClick={handleNavigate}
        sx={{
          maxWidth: compact ? '100%' : { xs: '100%', md: 360 },
          width: 'auto',
          minWidth: 0,
          flexShrink: 1,
          borderRadius: 999,
          fontFamily: 'Poppins, sans-serif',
          color: chipTheme ? chipTheme.fg : undefined,
          backgroundImage: chipTheme ? chipTheme.gradient : undefined,
          boxShadow: chipTheme ? chipTheme.shadow : undefined,
          border: chipTheme ? '1px solid rgba(255,255,255,0.24)' : undefined,
          backdropFilter: chipTheme ? 'blur(4px)' : undefined,
          '& .MuiChip-label': {
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            fontWeight: 600,
          },
        }}
        variant={chipTheme ? 'filled' : 'outlined'}
      />
    </Tooltip>
  );
}
